/**
 * CJdropshipping vs AliExpress 子页面 — 页面常量（全量文案）
 * 路由：/cjdropshipping-vs-aliexpress
 *
 * 【注意】
 * - AliExpress 是全球最大的 B2C 跨境电商平台（直接向消费者销售，无 dropshipping 专属功能）
 * - CJdropshipping 专为 dropshippers 设计：批量采购/私标/品牌包装/质检/无需消费者价格
 * - 图片使用 CDN 路径（resources/egg/alternatives/），函数接受 cdnHost 参数
 */

/**
 * 【注意】图片 CDN 路径：${cdnHost}/resources/egg/alternatives/filename
 * 参照 odm/index.jsx 模式，接受 cdnHost 参数
 */
export function getAliExpressConstants(cdnHost) {
  const img = (name) => `${cdnHost || ''}/resources/egg/alternatives/${name}`;
  return {
    // ========== VS_HERO: 子页 Banner ==========
    VS_HERO: {
      eyebrow: 'AliExpress Alternative',
      title: 'CJdropshipping vs',
      titleHighlight: 'AliExpress',
      subtitle: 'Looking for a Better Dropshipping AliExpress Alternative?',
      desc: 'CJdropshipping brings product sourcing, fulfillment, warehousing, POD, custom packaging, and branding together to help you grow faster and scale smarter.',
      proofItems: ['Built for dropshippers', 'No consumer pricing', 'Custom branding'],
      ctaText: 'Try for free',
      ctaUrl: '/home',
      note: 'Designed for serious dropshipping businesses, not retail shoppers.',
      heroImage: img('hero_banner2.jpg'),
      topChipText: 'Dropshipping-first platform',
      bottomCard: {
        label: 'Why CJdropshipping',
        title: 'Purpose-built for dropshipping, not retail shopping',
        desc: 'Bulk sourcing, quality inspection, custom packaging, and dedicated fulfillment — all designed around how dropshippers actually work.',
      },
    },

    // ========== SELLPOINTS: 卖点标签 ==========
    SELLPOINTS: [
      'Product Sourcing',
      'Global Fulfillment',
      'Quality Inspection',
      'Custom Packaging',
      'Print on Demand',
      'Global Warehouses',
      'Dedicated Agent Support',
    ],

    // ========== VS_TABLE: 三列对比表 ==========
    VS_TABLE: {
      kicker: 'Platform Comparison',
      title: '<strong>CJdropshipping</strong> vs AliExpress: Core Feature Comparison',
      desc: 'AliExpress is designed for retail shoppers. CJdropshipping is purpose-built for dropshippers with bulk pricing, no consumer retail experience, quality inspection, custom branding, and dedicated fulfillment operations.',
      summaryCards: [
        {
          icon: '1',
          title: 'More than marketplace sourcing',
          desc: 'AliExpress helps sellers find products, while CJdropshipping supports the full dropshipping workflow from sourcing to fulfillment.',
        },
        {
          icon: '2',
          title: 'Built for sellers',
          desc: 'CJdropshipping gives sellers sourcing, inspection, warehousing, shipping, POD, and packaging support in one platform.',
        },
        {
          icon: '3',
          title: 'Better for scaling',
          desc: 'CJdropshipping is stronger for sellers who want faster processing, quality control, and a better customer experience.',
        },
      ],
      headerFeature: 'Feature',
      headerCompetitor: 'AliExpress',
      headerCj: 'CJdropshipping',
      rows: [
        {
          feature: 'Product Positioning',
          competitor: 'Online retail marketplace',
          cj: 'Dropshipping sourcing and fulfillment platform',
        },
        {
          feature: 'Main Value',
          competitor: 'Product discovery and low-cost marketplace purchasing',
          cj: 'Sourcing, fulfillment, warehousing, POD, custom packaging, branding',
        },
        {
          feature: 'Built for Dropshipping',
          competitor: 'Not specifically built for dropshipping operations',
          cj: 'Yes, designed for dropshipping sellers',
        },
        {
          feature: 'Product Sourcing',
          competitor: 'Sellers browse existing marketplace listings',
          cj: 'Supports sourcing requests and supplier resources',
        },
        {
          feature: 'Order Fulfillment',
          competitor: 'Fulfillment depends on individual marketplace sellers',
          cj: 'Supports order processing, packing, shipping, and logistics',
        },
        {
          feature: 'Shipping Control',
          competitor: 'Shipping speed and quality vary by seller',
          cj: 'More centralized fulfillment and shipping support',
        },
        {
          feature: 'Quality Control',
          competitor: 'Depends heavily on individual suppliers',
          cj: 'Better suited for sourcing and fulfillment management',
        },
        {
          feature: 'Warehousing',
          competitor: 'Not a dedicated dropshipping warehousing platform',
          cj: 'Supports global warehouses and 3PL fulfillment',
        },
        {
          feature: 'Custom Packaging',
          competitor: 'Usually limited or supplier-dependent',
          cj: 'Supports custom packaging and branding services',
        },
        {
          feature: 'POD',
          competitor: 'Not the main platform focus',
          cj: 'Supports POD and customization',
        },
        {
          feature: 'Branding Support',
          competitor: 'Limited branding control',
          cj: 'Stronger support for branded ecommerce experience',
        },
        {
          feature: 'Best For',
          competitor: 'Beginners testing products or browsing low-cost items',
          cj: 'Sellers who want to source, fulfill, customize, brand, and scale',
        },
        {
          feature: 'Core Advantage',
          competitor: 'Large product marketplace with many low-cost items',
          cj: 'More complete dropshipping infrastructure',
        },
      ],
    },

    // ========== FIT: Who Should Choose CJdropshipping ==========
    FIT: {
      kicker: 'Seller Fit Guide',
      title: 'Who Should Choose <strong>CJdropshipping?</strong>',
      desc: 'CJdropshipping is a better fit for sellers who:',
      imageUrl: img('fit_who_should_choose.jpg'),
      badgeText: '✓ Best Fit for Dropshipping Businesses',
      floatTitle: 'Dropshipping-first ecommerce sellers',
      floatDesc: 'A stronger fit for sellers who want a platform built specifically around dropshipping, not retrofitted from a retail marketplace.',
      items: [
        'Need reliable product sourcing',
        'Want fulfillment support for dropshipping orders',
        'Sell through Shopify, WooCommerce, TikTok Shop, or eBay',
        'Want to test or sell POD products',
        'Need custom packaging or branding services',
        'Want to improve shipping and customer experience',
        'Plan to move from marketplace reselling to brand-based ecommerce',
        'See dropshipping as a long-term business, not just short-term product testing',
      ],
      summaryTitle: 'Why CJdropshipping stands out',
      summaryDesc: 'CJdropshipping is ideal for sellers who want to go beyond simple product copying. It helps you build a stronger business foundation through sourcing, fulfillment, customization, and branding support.',
    },

    // ========== ADVANTAGES: 核心优势 ==========
    ADVANTAGES: {
      kicker: 'Competitive Advantages',
      title: 'How <strong>CJdropshipping</strong> beat the competition',
      desc: 'CJdropshipping is a full dropshipping supply chain partner. It helps sellers go beyond product listing with sourcing, fulfillment, shipping, branding, and dedicated support.',
      cards: [
        {
          num: '01',
          title: 'Full Dropshipping Supply Chain Support',
          desc: 'CJdropshipping covers more than listing. It supports product sourcing, procurement, warehousing, fulfillment, shipping, and after-sales workflows.',
          imageUrl: img('adv_supply_chain.jpg'),
        },
        {
          num: '02',
          title: 'Reliable Product Sourcing',
          desc: 'Sellers can submit sourcing requests and find products through CJdropshipping\'s supplier resources, making it easier to discover better products and improve margins.',
          imageUrl: img('adv_sourcing.jpg'),
        },
        {
          num: '03',
          title: 'Quality Inspection Before Shipping',
          desc: 'CJdropshipping helps inspect products and packages before shipment, reducing the risk of defects, complaints, refunds, and negative reviews.',
          imageUrl: img('adv_quality_inspection.jpg'),
        },
        {
          num: '04',
          title: 'Global Warehousing',
          desc: 'With warehouse support in different markets, CJdropshipping helps sellers store inventory closer to customers and improve fulfillment efficiency.',
          imageUrl: img('adv_warehousing.jpg'),
        },
        {
          num: '05',
          title: 'End-to-End Order Fulfillment',
          desc: 'CJdropshipping can handle order processing, picking, packing, shipping, tracking, and delivery support, helping sellers reduce manual work.',
          imageUrl: img('adv_fulfillment.jpg'),
        },
        {
          num: '06',
          title: 'Global Shipping Solutions',
          desc: 'CJdropshipping provides global logistics options to help sellers deliver products to customers more efficiently and reliably.',
          imageUrl: img('adv_shipping.jpg'),
        },
        {
          num: '07',
          title: 'Custom Packaging and Branding',
          desc: 'Sellers can create branded packaging, inserts, labels, and unboxing experiences, making it easier to build a real ecommerce brand.',
          imageUrl: img('adv_custom_packaging.jpg'),
        },
        {
          num: '08',
          title: 'Dedicated Agent Support',
          desc: 'CJdropshipping provides service support to help sellers with sourcing, fulfillment, logistics, and operational issues as their business grows.',
          imageUrl: img('adv_dedicated_support.jpg'),
        },
      ],
      footerCta: {
        text: 'CJdropshipping gives dropshippers the platform they actually need: purpose-built pricing, centralized operations, quality control, and brand-building tools — none of which a retail marketplace like AliExpress was designed to provide.',
        ctaText: 'GET STARTED',
        ctaUrl: '/home',
      },
    },

    // ========== TESTIMONIALS: 买家评价 ==========
    TESTIMONIALS: {
      kicker: 'Real Seller Stories',
      title: 'Why Growing Sellers <strong>Stay with CJdropshipping</strong>',
      desc: 'Hear how ecommerce sellers use CJdropshipping to streamline sourcing, fulfillment, branding, and shipping while building stronger long-term businesses.',
      rows: [
        [
          {
            avatarUrl: img('32.jpeg'),
            name: 'Emily Carter',
            role: 'Shopify Store Owner',
            country: 'United States',
            text: 'CJdropshipping helped me simplify my fulfillment process. Instead of manually coordinating with multiple suppliers, I can manage sourcing, orders, and shipping in one place. It saves me a lot of time every week.',
          },
          {
            avatarUrl: img('12.jpeg'),
            name: 'Daniel Hughes',
            role: 'Ecommerce Entrepreneur',
            country: 'United Kingdom',
            text: 'As my store started growing, I needed more than just a product listing tool. CJdropshipping gave me access to sourcing, fulfillment, and packaging support, which made it easier to scale my business.',
          },
          {
            avatarUrl: img('47.jpeg'),
            name: 'Sofia Martinez',
            role: 'Dropshipping Seller',
            country: 'Spain',
            text: 'One of the biggest advantages for me is product sourcing. When I cannot find the right product, CJdropshipping helps me look for alternatives and connect with supply options that fit my store.',
          },
        ],
        [
          {
            avatarUrl: img('15.jpeg'),
            name: 'Liam Walker',
            role: 'DTC Brand Founder',
            country: 'Australia',
            text: 'I wanted my store to feel more professional, and CJdropshipping\'s custom packaging and branding options helped a lot. It made my customer experience feel less generic.',
          },
          {
            avatarUrl: img('5.jpeg'),
            name: 'Chloe Anderson',
            role: 'Online Store Operator',
            country: 'Canada',
            text: 'Managing products, orders, and fulfillment through one platform makes daily operations much easier. CJdropshipping is especially helpful if you want to reduce repetitive manual work.',
          },
          {
            avatarUrl: img('60.jpeg'),
            name: 'Marco Rossi',
            role: 'Ecommerce Seller',
            country: 'Italy',
            text: 'CJdropshipping feels like a better fit for sellers who want to build a serious dropshipping business. It is not only about listing products, but also about improving sourcing, shipping, and the overall customer experience.',
          },
        ],
      ],
    },

    // ========== VS_FAQ: 子页专属 FAQ ==========
    VS_FAQ: {
      kicker: 'FAQ',
      title: 'AliExpress vs <strong>CJdropshipping</strong> FAQ',
      intro:
        'Get quick answers about how CJdropshipping compares with AliExpress for dropshipping — covering pricing, quality, fulfillment, branding, and building a long-term business.',
      sideCard: {
        label: 'Quick Decision Guide',
        title: 'Need a platform built for dropshippers?',
        desc: 'AliExpress is a retail marketplace for consumers. CJdropshipping is purpose-built for dropshipping businesses, with specific tools for sourcing, pricing, inspection, fulfillment, packaging, and scaling.',
        miniItems: [
          { text: 'Dropshipping-specific pricing and sourcing' },
          { text: 'Quality inspection before shipping' },
          { text: 'Custom packaging and branding support' },
        ],
      },
      items: [
        {
          q: 'Is CJdropshipping an AliExpress alternative?',
          a: 'Yes. CJdropshipping can be an AliExpress alternative for sellers who need more than marketplace product sourcing. It is better suited for sourcing, fulfillment, warehousing, POD, custom packaging, 3PL fulfillment, and branding support.',
        },
        {
          q: 'What is the biggest difference between CJdropshipping and AliExpress?',
          a: 'AliExpress is mainly a retail marketplace where sellers can browse and buy products from many suppliers. CJdropshipping is a dropshipping sourcing and fulfillment platform built to support ecommerce sellers with sourcing, fulfillment, shipping, warehousing, and branding services.',
        },
        {
          q: 'Is AliExpress good for dropshipping?',
          a: 'AliExpress can be useful for product research and early-stage product testing. However, sellers may face challenges with shipping speed, supplier consistency, quality control, and branding. CJdropshipping is better suited for sellers who want a more reliable and scalable workflow.',
        },
        {
          q: 'Is CJdropshipping better for fulfillment?',
          a: 'Yes. CJdropshipping is more fulfillment-oriented because it supports sourcing, order processing, warehousing, packing, shipping, and related dropshipping services.',
        },
        {
          q: 'Which one is better for Shopify sellers?',
          a: 'AliExpress can be useful for finding products, but CJdropshipping is usually a better fit for Shopify sellers who need sourcing, fulfillment, product import, custom packaging, POD, and brand-building support.',
        },
        {
          q: 'Which one is better for branding?',
          a: 'CJdropshipping is better aligned with branding needs because it supports custom packaging, POD, white label options, and other brand experience services. AliExpress orders are usually more generic and supplier-dependent.',
        },
        {
          q: 'Who should choose CJdropshipping instead of AliExpress?',
          a: 'CJdropshipping is better for sellers who want more control over product sourcing, fulfillment, shipping, packaging, and customer experience. It is especially suitable for sellers who want to scale beyond basic AliExpress product testing.',
        },
      ],
      bottomText: 'Ready to move beyond a retail marketplace? Start with a free account and see how CJdropshipping works for your dropshipping business.',
      bottomCtaText: 'Try CJdropshipping Free',
      bottomCtaUrl: '/home',
    },

    // ========== BOTTOM_CTA: 底部行动召唤 ==========
    BOTTOM_CTA: {
      kicker: 'Ready to Scale Smarter?',
      title: 'Ready to move from marketplace sourcing to complete dropshipping fulfillment?',
      desc: 'Start with CJdropshipping and build a more scalable ecommerce business.',
      ctaText: 'Upgrade from AliExpress to CJdropshipping',
      ctaUrl: '/home',
      bgImage: img('hero_banner.jpg'),
    },
  };
}
